/**
 * TokenSavingsChart — ahorro de tokens/coste por día (dashboard principal).
 *
 * 19-Oct-2026 (REQ-CHAT-039 §2/§7): a la derecha de «Uso de agentes».
 *
 * Tipo de gráfico elegido: **barras diarias apiladas** (coste efectivo +
 * ahorro = coste contrafactual) con **línea de % de ahorro** en eje derecho.
 * El dinero es la métrica principal (KPI de coste, D4) y los tokens viajan en
 * el tooltip (producido/enviado) para el detalle técnico.
 */

import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useI18n } from '@/i18n';
import { useTenantTokenSavings } from '@/hooks/use-tenant-token-savings';

interface TokenSavingsChartProps {
  tenantId?: string;
  title?: string;
}

/** Paleta aprobada — los mismos tonos que AgentUsageChart (purple/green/yellow). */
const PALETTE = {
  primary: '#8884d8', // serie principal (coste efectivo / enviado)
  secondary: '#82ca9d', // serie secundaria (ahorro)
  tertiary: '#ffc658', // línea de % ahorro
} as const;

export function TokenSavingsChart({ tenantId, title }: TokenSavingsChartProps) {
  const { t } = useI18n();
  const { data, isLoading, isError } = useTenantTokenSavings(tenantId, '7d', 'cost');

  const chartData = useMemo(() => {
    const series = data?.series ?? [];
    return series.map((point) => {
      const effective = Number(point.cost_effective ?? 0);
      const counterfactual = Number(point.cost_counterfactual ?? 0);
      const saved = Math.max(0, counterfactual - effective);
      const produced = Number(point.produced ?? 0);
      const sent = Number(point.sent ?? 0);
      const savedTokens = Math.max(0, produced - sent);
      const pct = counterfactual > 0 ? (saved / counterfactual) * 100 : null;
      return {
        day: point.bucket,
        effective: Number(effective.toFixed(6)),
        saved: Number(saved.toFixed(6)),
        counterfactual,
        pct: pct === null ? null : Number(pct.toFixed(1)),
        produced,
        sent,
        savedTokens,
      };
    });
  }, [data]);

  // 19-Oct: sin tarifas cargadas (rate_missing) el dinero es 0 — se muestra el
  // ahorro en TOKENS para que el panel nunca quede «vacío».
  const moneyAvailable = useMemo(
    () => chartData.some((point) => point.counterfactual > 0),
    [chartData],
  );

  // 19-Oct: sin datos no se muestra nada (nada de placeholders).
  if (isLoading || isError || chartData.length === 0) {
    return null;
  }

  return (
    <Card data-testid="token-savings-chart-card">
      <CardHeader>
        <CardTitle>{title ?? t('admin.dashboard.charts.tokenSavings')}</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 && (
          <div className="h-64" data-testid="token-savings-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis
                  yAxisId="money"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v: number) =>
                    moneyAvailable ? `$${v.toFixed(3)}` : `${v}`
                  }
                />
                <YAxis
                  yAxisId="pct"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v: number) => `${v}%`}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === t('admin.dashboard.savings.pct')) {
                      return [`${value}%`, name];
                    }
                    if (!moneyAvailable) {
                      return [Number(value).toLocaleString(), name];
                    }
                    return [`$${Number(value).toFixed(4)}`, name];
                  }}
                  labelFormatter={(label, payload) => {
                    const row = (payload?.[0]?.payload ?? {}) as {
                      produced?: number;
                      sent?: number;
                    };
                    return `${label} · ${t('admin.dashboard.savings.tokens')}: ${row.produced ?? 0}→${row.sent ?? 0}`;
                  }}
                />
                <Legend />
                {moneyAvailable ? (
                  <>
                    <Bar
                      yAxisId="money"
                      dataKey="effective"
                      stackId="cost"
                      fill={PALETTE.primary}
                      name={t('admin.dashboard.savings.effective')}
                    />
                    <Bar
                      yAxisId="money"
                      dataKey="saved"
                      stackId="cost"
                      fill={PALETTE.secondary}
                      name={t('admin.dashboard.savings.saved')}
                    />
                    <Line
                      yAxisId="pct"
                      type="monotone"
                      dataKey="pct"
                      stroke={PALETTE.tertiary}
                      strokeWidth={2}
                      dot={false}
                      name={t('admin.dashboard.savings.pct')}
                    />
                  </>
                ) : (
                  <>
                    {/* Tokens: enviado (base) + ahorrado (producido − enviado) apilados
                        → total = producido (equivalente al contrafactual de coste). */}
                    <Bar
                      yAxisId="money"
                      dataKey="sent"
                      stackId="tokens"
                      fill={PALETTE.primary}
                      name={t('admin.dashboard.savings.sent')}
                    />
                    <Bar
                      yAxisId="money"
                      dataKey="savedTokens"
                      stackId="tokens"
                      fill={PALETTE.secondary}
                      name={t('admin.dashboard.savings.saved')}
                    />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
